import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/KeycloakContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import TenantEdit from './pages/TenantEdit';
import MumbleServers from './pages/MumbleServers';
import MumbleServerEdit from './pages/MumbleServerEdit';
import TemplatesPage from './pages/TemplatesPage';
import TrialPage from './pages/TrialPage';
import SystemConfig from './pages/SystemConfig';
import { AlertTriangle } from 'lucide-react';

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-500">Authentifizierung...</p>
      </div>
    </div>
  );
}

function KeycloakOfflineBanner() {
  return (
    <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2">
      <div className="flex items-center justify-center gap-2 text-yellow-700 text-sm">
        <AlertTriangle size={16} />
        <span>Keycloak offline - Sie arbeiten mit einer zwischengespeicherten Session</span>
      </div>
    </div>
  );
}

function ProtectedApp() {
  const { initialized, authenticated, keycloakOnline, login } = useAuth();

  if (!initialized) {
    return <LoadingScreen />;
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center bg-white border border-slate-200 rounded-xl p-8 max-w-md shadow-sm">
          <h1 className="text-xl font-bold text-slate-800 mb-4">Anmeldung erforderlich</h1>
          {!keycloakOnline ? (
            <>
              <p className="text-red-600 mb-4">
                Der Authentifizierungsserver ist nicht erreichbar.
              </p>
              <p className="text-slate-500 mb-6">
                Bitte versuchen Sie es später erneut oder kontaktieren Sie den Administrator.
              </p>
            </>
          ) : (
            <p className="text-slate-500 mb-6">
              Sie werden zur Anmeldeseite weitergeleitet...
            </p>
          )}
          <button
            onClick={login}
            className="bg-sayses-accent hover:bg-sayses-accent-hover text-white px-6 py-2 rounded-lg transition-colors"
          >
            Anmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {!keycloakOnline && <KeycloakOfflineBanner />}
      <div className="flex-1 overflow-hidden">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="tenants" element={<Tenants />} />
              <Route path="tenants/new" element={<TenantEdit />} />
              <Route path="tenants/:id/edit" element={<TenantEdit />} />
              <Route path="mumble-servers" element={<MumbleServers />} />
              <Route path="mumble-servers/new" element={<MumbleServerEdit />} />
              <Route path="mumble-servers/:id/edit" element={<MumbleServerEdit />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="trial" element={<TrialPage />} />
              <Route path="system-config" element={<SystemConfig />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProtectedApp />
    </AuthProvider>
  );
}

export default App;
